import PasswordService from "../../../services/password";
import { i18n } from "@/boot/i18n";
import { MAX_LENGTH, MIN_LENGTH, REQUIRED } from "../validator/methods";
import * as types from "./mutationTypes";
import ErrorHandler from "@/tools/errorHandler";

export default {
  initRecoveryState({ dispatch }) {
    const validator = recoveryValidator();
    dispatch("validator/addValidator", validator.email, { root: true });
  },
  clearRecoveryState({ dispatch, commit }) {
    dispatch("validator/clearValidator", null, { root: true });
    commit(types.CLEAR_EMAIL);
  },
  initResetState({ dispatch }) {
    dispatch("addPasswordValidators");
  },
  clearResetState({ dispatch, commit }) {
    commit(types.CLEAR_PASSWORD);
    commit(types.CLEAR_CONFIRM_PASSWORD);
    dispatch("validator/clearValidator", null, { root: true });
  },
  initUpdateState({ dispatch }) {
    dispatch("addPasswordValidators");
  },
  clearUpdateState({ dispatch, commit }) {
    commit(types.CLEAR_PASSWORD);
    commit(types.CLEAR_CONFIRM_PASSWORD);
    commit(types.CLEAR_CURRENT_PASSWORD);
    dispatch("validator/clearValidator", null, { root: true });
  },
  async update({ dispatch, state }) {
    const valid = await dispatch("validatePassword");
    if (!valid) {
      return;
    }
    try {
      const data = {
        current_password: state.currentPassword,
        new_password: state.password
      };
      await PasswordService.update(data);
      dispatch("ui/showToastSuccess", i18n.t("general.messages.password_change"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async recovery({ dispatch, rootState, state }) {
    const validation = {
      valid: true
    };
    await dispatch("validator/validate", { field: "email", value: state.email }, { root: true });
    if (rootState.validator.errors?.length !== 0) {
      validation.valid = false;
      return validation;
    }
    try {
      await PasswordService.recovery(state.email);
      dispatch("setPasswordSuccess");
    } catch (e) {
      dispatch("setPasswordError");
    }
    return validation;
  },
  async reset({ dispatch }, data) {
    const validation = {
      valid: true
    };
    const valid = await dispatch("validatePassword");
    if (!valid) {
      validation.valid = false;
      return validation;
    }
    try {
      await PasswordService.reset(data);
      dispatch("setPasswordSuccess");
    } catch (e) {
      dispatch("setPasswordError");
    }
    return validation;
  },
  setPasswordSuccess({ commit }) {
    commit(types.CLEAR_ERROR);
  },
  setPasswordError({ commit }) {
    commit(types.SET_ERROR);
  },
  setEmail({ commit, dispatch }, email) {
    commit(types.SET_EMAIL, email);
    dispatch("validator/checkValidation", { field: "email", value: email }, { root: true });
  },
  setPassword({ commit, dispatch }, password) {
    commit(types.SET_PASSWORD, password);
    dispatch("validator/checkValidation", { field: "password", value: password }, { root: true });
  },
  setConfirmPassword({ commit, dispatch, state }, confirm) {
    commit(types.SET_CONFIRM_PASSWORD, confirm);
    dispatch("validator/checkValidation", { field: "confirmPassword", value: confirm }, { root: true });
    dispatch(
      "validator/checkValidation",
      { field: "equal", value: { password: state.password.trim(), confirmPassword: state.confirmPassword.trim() } },
      { root: true }
    );
  },
  setCurrentPassword({ commit }, password) {
    commit(types.SET_CURRENT_PASSWORD, password);
  },
  addPasswordValidators({ dispatch }) {
    const validator = passwordValidator();
    dispatch("validator/addValidator", validator.password, { root: true });
    dispatch("validator/addValidator", validator.confirmPassword, { root: true });
    dispatch("validator/addValidator", validator.equal, { root: true });
  },
  validatePassword({ dispatch, rootState, state }) {
    dispatch("validator/validate", { field: "password", value: state.password }, { root: true });
    dispatch("validator/validate", { field: "confirmPassword", value: state.confirmPassword }, { root: true });
    dispatch(
      "validator/validate",
      { field: "equal", value: { password: state.password, confirmPassword: state.confirmPassword } },
      { root: true }
    );
    return rootState.validator.errors?.length === 0;
  }
};

const recoveryValidator = () => {
  return {
    email: {
      field: "email",
      validations: [{ method: REQUIRED, message: i18n.t("general.rules.required") }]
    }
  };
};

const passwordValidator = () => {
  return {
    password: {
      field: "password",
      validations: [
        { method: REQUIRED, message: i18n.t("general.rules.required") },
        { method: MIN_LENGTH, value: 8, message: i18n.t("general.rules.min_password") },
        { method: MAX_LENGTH, value: 30, message: i18n.t("general.rules.max_password") }
      ]
    },
    confirmPassword: {
      field: "confirmPassword",
      validations: [
        { method: REQUIRED, message: i18n.t("general.rules.required") },
        { method: MIN_LENGTH, value: 8, message: i18n.t("general.rules.min_password") },
        { method: MAX_LENGTH, value: 30, message: i18n.t("general.rules.max_password") }
      ]
    },
    equal: {
      field: "equal",
      validations: [{ method: validatePasswordsEqual, message: i18n.t("password.messages.password_dont_match") }]
    }
  };
};

function validatePasswordsEqual({ password, confirmPassword }) {
  return password === confirmPassword;
}
